import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { toast } from 'sonner'
import { csrfHeaders } from '@/lib/csrf'
import { Search, Loader2, AlertCircle, CheckCircle2, Info } from 'lucide-react'

interface BuyDomainDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId: number
}

interface DomainSearchResult {
  domain: string
  available: boolean
  message: string
  price_credits?: number
  registration_years?: number
  organization_credits: number
}

export function BuyDomainDialog({
  open,
  onOpenChange,
  projectId,
}: BuyDomainDialogProps) {
  const { t } = useTranslation('conversations')
  const [domainInput, setDomainInput] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isPurchasing, setIsPurchasing] = useState(false)
  const [searchResult, setSearchResult] = useState<DomainSearchResult | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)

  const handleSearch = async () => {
    if (!domainInput.trim()) {
      setSearchError(t('domain.toasts.enterDomainName'))
      return
    }

    setIsSearching(true)
    setSearchError(null)
    setSearchResult(null)

    try {
      const response = await fetch(
        `/projects/${projectId}/custom_domain/search?domain=${encodeURIComponent(domainInput.trim())}`,
        {
          headers: {
            'Accept': 'application/json',
          },
        }
      )

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setSearchResult(data)
        } else {
          setSearchError(data.error || t('domain.buyDialog.searchFailed'))
        }
      } else {
        const data = await response.json()
        setSearchError(data.error || t('domain.buyDialog.searchFailed'))
      }
    } catch (error) {
      console.error('Domain search error:', error)
      setSearchError(t('domain.toasts.networkErrorSearch'))
    } finally {
      setIsSearching(false)
    }
  }

  const handlePurchase = async () => {
    if (!searchResult?.available) return

    setIsPurchasing(true)

    try {
      const response = await fetch(`/projects/${projectId}/custom_domain`, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          domain: searchResult.domain,
        }),
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(t('domain.toasts.domainPurchaseSuccess'))
        onOpenChange(false)
        // Reset form
        setDomainInput('')
        setSearchResult(null)
        // Reload to show updated project data
        router.reload()
      } else {
        toast.error(data.errors?.join(', ') || t('domain.toasts.domainPurchaseError'))
      }
    } catch (error) {
      console.error('Domain purchase error:', error)
      toast.error(t('domain.toasts.networkErrorPurchase'))
    } finally {
      setIsPurchasing(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('domain.buyDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('domain.buyDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search Input */}
          <div className="space-y-2">
            <Label htmlFor="domain-search">{t('domain.buyDialog.domainLabel')}</Label>
            <div className="flex gap-2">
              <Input
                id="domain-search"
                placeholder={t('domain.buyDialog.domainPlaceholder')}
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={isSearching || isPurchasing}
                className="flex-1"
              />
              <Button
                onClick={handleSearch}
                disabled={isSearching || isPurchasing || !domainInput.trim()}
                size="icon"
                className="flex-shrink-0"
              >
                {isSearching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('domain.buyDialog.domainHint')}
            </p>
          </div>

          {/* Search Error */}
          {searchError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-3 w-full">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0 overflow-hidden">
                <p className="text-sm font-medium text-red-900 dark:text-red-300">
                  {t('domain.buyDialog.searchFailed')}
                </p>
                <p className="text-xs text-red-700 dark:text-red-400 mt-1" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {searchError}
                </p>
              </div>
            </div>
          )}

          {/* Search Result - Available */}
          {searchResult && searchResult.available && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 w-full">
              <div className="flex items-center justify-between gap-4 w-full">
                <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <p className="text-sm font-semibold text-green-900 dark:text-green-300" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {t('domain.buyDialog.available', { domain: searchResult.domain })}
                  </p>
                </div>
                {searchResult.price_credits !== undefined && (
                  <span className="text-sm font-semibold text-green-900 dark:text-green-100 whitespace-nowrap flex-shrink-0">
                    {searchResult.price_credits} {t('domain.buyDialog.credits')}
                    {searchResult.registration_years && searchResult.registration_years > 1 && (
                      <span className="font-normal text-green-700 dark:text-green-300">
                        {' '}{t('domain.buyDialog.forYears', { count: searchResult.registration_years })}
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Search Result - Not Available */}
          {searchResult && !searchResult.available && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 w-full">
              <div className="flex items-center gap-3 min-w-0 overflow-hidden w-full">
                <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-sm font-semibold text-amber-900 dark:text-amber-300" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                  {t('domain.buyDialog.notAvailable', { domain: searchResult.domain })}
                </p>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-3">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
              {t('domain.buyDialog.infoText')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPurchasing}
          >
            {t('common:cancel')}
          </Button>
          <Button
            type="button"
            onClick={handlePurchase}
            disabled={!searchResult?.available || isPurchasing}
          >
            {isPurchasing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('domain.buyDialog.purchasing')}
              </>
            ) : (
              t('domain.buyDialog.purchaseDomain')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
